import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  Schedule,
  Cancel,
  Archive,
  Edit,
} from '@mui/icons-material';
import { ApprovalStatus } from '../../api/data-contracts';

interface ApprovalStatusBadgeProps extends Omit<ChipProps, 'color' | 'label' | 'icon'> {
  status: ApprovalStatus;
  size?: 'small' | 'medium';
}

export const ApprovalStatusBadge: React.FC<ApprovalStatusBadgeProps> = ({
  status,
  size = 'small',
  ...chipProps
}) => {
  const { t } = useTranslation();

  const getStatusConfig = (status: ApprovalStatus) => {
    switch (status) {
      case ApprovalStatus.Draft:
        return {
          label: t('faq.status.draft'),
          color: 'default' as const,
          icon: <Edit />,
        };
      case ApprovalStatus.PendingReview:
        return {
          label: t('faq.status.pending_review'),
          color: 'warning' as const,
          icon: <Schedule />,
        };
      case ApprovalStatus.Approved:
        return {
          label: t('faq.status.approved'),
          color: 'success' as const,
          icon: <CheckCircle />,
        };
      case ApprovalStatus.Rejected:
        return {
          label: t('faq.status.rejected'),
          color: 'error' as const,
          icon: <Cancel />,
        };
      case ApprovalStatus.Archived:
        return {
          label: t('faq.status.archived'),
          color: 'secondary' as const,
          icon: <Archive />,
        };
      default:
        return {
          label: t('faq.status.unknown'),
          color: 'default' as const,
          icon: <Edit />,
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      {...chipProps}
      label={config.label}
      color={config.color}
      icon={config.icon}
      size={size}
      variant="outlined"
    />
  );
};